import { JSX, lazy } from "react";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import Home from "../pages/Home/Home";
import Login from "../pages/Login/Login";
import NotFound from "../pages/NotFound/NotFound";
import { ModalConfig } from "../components/Contexts/GlobalModalContext";
import { RoleConfig } from "../DTOs/Settings/RoleConfigDto";
import DiggingLoadingSummaryPage from "../pages/ChatreeProduction/DiggingLoadingSummaryPage/DiggingLoadingSummaryPage";
import Production from "../pages/ChatreeProduction/Production";
import GeologyCrushing from "../pages/GeologyCrushing/GeologyCrushing";
import GeologyRehandle from "../pages/GeologyRehandle/GeologyRehandle";
import Operations from "../pages/Operations/Operations";
import Stoppages from "../pages/Stoppages/Stoppages";

const Analytes = lazy(() => import("../pages/Admin/References/Analytes/Analytes"));
const BlastMaterial = lazy(() => import("../pages/Admin/References/Blasts/BlastMaterial"));
const BlastMaterialType = lazy(() => import("../pages/Admin/References/Blasts/BlastMaterialType"));
const BlastPattern = lazy(() => import("../pages/Admin/References/Blasts/BlastPattern/BlastPattern"));
const BucketFactors = lazy(() => import("../pages/Admin/References/BucketFactors/BucketFactors"));
const BucketTypes = lazy(() => import("../pages/Admin/References/BucketTypes/BucketTypes"));
const Densities = lazy(() => import("../pages/Admin/References/Densities/Densities"));
const BlastHoleDrilling = lazy(() => import("../pages/Drilling/BlastHoleDrilling/BlastHoleDrilling"));
const BlastHoleDrillingList = lazy(() => import("../pages/Drilling/BlastHoleDrilling/BlastHoleDrillingList"));
const GeologyMilling = lazy(() => import("../pages/GeologyMilling/GeologyMilling"));
const GeologyOreBlocks = lazy(() => import("../pages/GeologyOreBlocks/GeologyOreBlocks"));
const GeologyOreTally = lazy(() => import("../pages/GeologyOreTally/GeologyOreTally"));
const RoyaltySplits = lazy(() => import("../pages/RoyaltySplits/RoyaltySplits"));
const StockpileBalances = lazy(() => import("../pages/StockpileBalances/StockpileBalances"));
const Departments = lazy(() => import("../pages/Admin/References/Departments/Departments"));
const MaterialTypes = lazy(() => import("../pages/Admin/References/MaterialTypes/MaterialTypes"));
const Plants = lazy(() => import("../pages/Admin/References/Plants/Plants"));
const DowntimeSubtypes = lazy(() => import("../pages/Admin/References/DowntimeSubTypes/DownTimeSubTypes"));
const PitStages = lazy(() => import("../pages/Admin/References/PitStages/PitStages"));
const Targets = lazy(() => import("../pages/Admin/References/Targets/Targets"));
const MinePeriods = lazy(() => import("../pages/Admin/References/MinePeriods/MinePeriods"));
const DashboardTiles = lazy(() => import("../pages/Admin/References/DashboardTiles/DashboardTiles"));
const SectionNames = lazy(() => import("../pages/Admin/References/SectionNames/SectionNames"));
const TargetTypes = lazy(() => import("../pages/Admin/References/TargetTypes/TargetTypes"));
const DailyReport = lazy(() => import("../pages/Reports/DailyReport/DailyReport"));
const GeologyDrilling = lazy(() => import("../pages/GeologyDrilling/GeologyDrilling"));
const Users = lazy(() => import("../pages/Admin/References/Users/Users"));
const MaterialTypeClasses = lazy(() => import("../pages/Admin/References/MaterialTypeClasses/MaterialTypeClasses"));
const PlantTypes = lazy(() => import("../pages/Admin/References/PlantTypes/PlantTypes"));
const Locations = lazy(() => import("../pages/Admin/References/Locations/Locations"));
const DeviceMappings = lazy(() => import("../pages/Admin/References/DeviceMappings/DeviceMappings"));
const Dewatering = lazy(() => import("../pages/Dewatering/Dewatering"));
const GeologyRecalcModal = lazy(() => import("../pages/GeologyRecalcModal/GeologyRecalcModal").then(m => ({ default: m.GeologyRecalcModal })));
const MorningReport = lazy(() => import("../pages/Reports/MorningReport/MorningReport"));
const AccountPage = lazy(() => import("../pages/Account/Account"));
const ApiKeys = lazy(() => import("../pages/Account/ApiKeys/ApiKeys"));
const RoyaltyAreaBalances = lazy(() => import("../pages/RoyaltyAreaBalances/RoyaltyAreaBalances").then(m => ({ default: m.RoyaltyAreaBalances })));
const GeologyProduction = lazy(() => import("../pages/GeologyProduction/GeologyProduction/GeologyProduction"));
const GeoMiningMvtSummaryList = lazy(() => import("../pages/GeologyProduction/GeoMiningMvtSummaryList/GeoMiningMvtSummaryList"));
const TargetImportList = lazy(() => import("../pages/Targets/Import/TargetImportList"));
const TargetImportTemplateEditor = lazy(() => import("../pages/Targets/Import/TargetImportTemplateEditor"));
const TargetImportRun = lazy(() => import("../pages/Targets/Import/TargetImportRun"));
const TargetImportAnalyze = lazy(() => import("../pages/Targets/Import/TargetImportAnalyze"));
const ReportLibraryPage = lazy(() => import("../pages/Reports/ReportLibraryPage/ReportLibraryPage"));
const ReportBuilderPage = lazy(() => import("../pages/Reports/ReportBuilderPage/ReportBuilderPage"));
const ReportViewPage = lazy(() => import("../pages/Reports/ReportViewPage/ReportViewPage"));
const DataConnectionsPage = lazy(() => import("../pages/Reports/DataConnectionsPage/DataConnectionsPage"));
const MachinePositionsMap = lazy(() => import("../pages/Telemetry/MachinePositionsMap"));
const UncheckedGpsStoppages = lazy(() => import("../pages/Stoppages/UncheckedGpsStoppages"));

type RouteShell = { path: string; isShiftRelatedPage?: boolean; children?: RouteShell[] };

// Note: When adding a new shift-related route to createAppRoutes, add it here too.
const ROUTE_SHELLS: readonly RouteShell[] = [
  { path: "operations", isShiftRelatedPage: true },
  {
    path: "production", children: [
      { path: "show", isShiftRelatedPage: true },
      { path: "rehandle", isShiftRelatedPage: true },
      { path: "crushing", isShiftRelatedPage: true },
      { path: "ore-tally", isShiftRelatedPage: true },
    ]
  },
  { path: "stoppages", isShiftRelatedPage: true },
  { path: "unchecked-gps-stoppages", isShiftRelatedPage: true },
  {
    path: "db", children: [
      { path: "bh-drilling", isShiftRelatedPage: true },
      { path: "dewatering", isShiftRelatedPage: true },
    ]
  },
  {
    path: "geology", children: [
      { path: "production", isShiftRelatedPage: true },
      { path: "ore-tally", isShiftRelatedPage: true },
      { path: "milling", isShiftRelatedPage: true },
      { path: "drilling", isShiftRelatedPage: true },
    ]
  },
];

function collectShiftRelatedPaths(shells: readonly RouteShell[], parent = ""): string[] {
  return shells.flatMap(({ path, isShiftRelatedPage, children }) => {
    const full = `${parent}/${path}`;
    return [
      ...(isShiftRelatedPage ? [full] : []),
      ...(children ? collectShiftRelatedPaths(children, full) : []),
    ];
  });
}

export const SHIFT_RELATED_PAGE_PATHS: ReadonlySet<string> = new Set(collectShiftRelatedPaths(ROUTE_SHELLS));

export interface AppRouteConfig {
  path: string;
  label?: string;
  icon?: IconDefinition; // Note: Optional per-route override; otherwise resolved from routeIcons.ts by path
  ignore?: boolean;
  element?: JSX.Element;
  action?: (showModal: (config: ModalConfig) => void) => void; // For routes that trigger an action instead of navigation
  protected?: boolean;
  area?: string; // Note: page access grant; the primary mechanism for department-gated pages
  access?: string[]; // Note: literal role-name OR check, for privilege roles rather than page access
  accessAll?: string[]; // Note: literal role-name AND check, same scope as access
  superZoneAdminOnly?: boolean; // Note: gated on the data-driven Super Zone (roleConfig.superZoneRoleNames), not a hardcoded role
  children?: AppRouteConfig[];
}

export function createAppRoutes({ privilegeRoles, telemetryEnabled }: RoleConfig): AppRouteConfig[] {
  const { admin } = privilegeRoles;

  const perms = {
    ADMIN: [admin],
  };

  return [
    {
      path: "/login",
      element: <Login />,
      protected: false,
      ignore: true
    },
    {
      path: "/",
      element: <Home />,
      protected: true,
      label: "Home"
    },
    {
      path: "/account",
      element: <AccountPage />,
      protected: true,
      ignore: true,
      label: "Account"
    },
    {
      path: "/api-keys",
      element: <ApiKeys />,
      protected: true,
      ignore: true,
      label: "API Keys",
      access: perms.ADMIN
    },
    {
      path: "operations", element: <Operations />, protected: true,
      area: "operations", label: "Operations List"
    },
    {
      path: "production", protected: true,
      children: [
        {
          path: "show",
          element: <Production />,
          protected: true,
          area: "production/show",
          label: "Add/Edit Production"
        },
        {
          path: "list",
          element: <DiggingLoadingSummaryPage />,
          protected: true,
          area: "production/list",
          label: "Production List"
        },
        {
          path: "rehandle", element: <GeologyRehandle />, protected: true,
          area: "production/rehandle", label: "Rehandle"
        },
        {
          path: "crushing", element: <GeologyCrushing />, protected: true,
          area: "production/crushing", label: "Crushing"
        },
        {
          path: "ore-tally", element: <GeologyOreTally />, protected: true,
          area: "production/ore-tally", label: "Ore Tally"
        },
      ]
    },
    {
      path: "stoppages", element: <Stoppages />, protected: true,
      area: "stoppages"
    },
    // Note: absent telemetryEnabled reads as off, matching device-mappings below - no menu entry and no
    // reachable URL on a site with no FMS, rather than a route the nav merely hides.
    ...(telemetryEnabled ? [{
      path: "unchecked-gps-stoppages", element: <UncheckedGpsStoppages />, protected: true,
      area: "unchecked-gps-stoppages", label: "Unchecked GPS"
    }] : []),
    {
      path: "machine-positions", element: <MachinePositionsMap />, protected: true,
      area: "machine-positions", label: "Machine Positions"
    },
    {
      path: "db", protected: true,
      label: "Drill & Blast",
      children: [
        {
          path: "bh-drilling", element: <BlastHoleDrilling />, protected: true,
          area: "db/bh-drilling", label: "Add/Edit Blast Hole Drilling"
        },
        {
          path: "bh-drilling-list", element: <BlastHoleDrillingList />, protected: true,
          area: "db/bh-drilling-list", label: "Blast Hole Drilling List"
        },
        {
          path: "blasts",
          protected: true,
          children: [
            {
              path: "pattern", element: <BlastPattern />, protected: true,
              area: "db/blasts/pattern", label: "Patterns"
            },
            {
              path: "material", element: <BlastMaterial />, protected: true,
              area: "db/blasts/material", label: "Materials"
            },
            {
              path: "material-type", element: <BlastMaterialType />, protected: true,
              area: "db/blasts/material-type", label: "Material Types"
            }
          ]
        },
        {
          path: "dewatering", element: <Dewatering />, protected: true,
          area: "db/dewatering"
        },
      ]
    },
    {
      path: "geology", protected: true,
      children: [
        {
          path: "unused", protected: true,
          action: (showModal) => showModal({ content: () => <GeologyRecalcModal /> }),
          label: "Recalculate Stockpiles", area: "geology/unused", accessAll: [admin]
        },
        {
          path: "production", element: <GeologyProduction />, protected: true,
          area: "geology/production", label: "Show Mining Mvt"
        },
        {
          path: "mining-movements", element: <GeoMiningMvtSummaryList />, protected: true,
          area: "geology/mining-movements", label: "Mining Mvt List"
        },
        {
          path: "stockpile-balances", element: <StockpileBalances />, protected: true,
          area: "geology/stockpile-balances", label: "Stockpile Balances"
        },
        {
          path: "ore-blocks", element: <GeologyOreBlocks />, protected: true,
          area: "geology/ore-blocks", label: "Ore Blocks"
        },
        {
          path: "ore-tally", element: <GeologyOreTally />, protected: true,
          area: "geology/ore-tally", label: "Ore Tally"
        },
        {
          path: "milling", element: <GeologyMilling />, protected: true,
          area: "geology/milling"
        },
        {
          path: "drilling", element: <GeologyDrilling />, protected: true,
          area: "geology/drilling"
        },
      ]
    },
    {
      path: "reports",
      protected: true,
      children: [
        {
          path: "daily", element: <DailyReport />, protected: true,
          label: "Daily Report", area: "reports/daily",
        },
        {
          path: "morning", element: <MorningReport />, protected: true,
          label: "Morning Meeting Report", area: "reports/morning",
        },
        {
          path: "library", element: <ReportLibraryPage />, protected: true,
          label: "Report Library", area: "reports/library",
        },
        {
          path: "builder", element: <ReportBuilderPage />, protected: true,
          label: "Eclipse Report Builder", area: "reports/builder", accessAll: [admin],
        },
        {
          path: "view/:id", element: <ReportViewPage />, protected: true,
          ignore: true, area: "reports/view",
        },
        {
          path: "data-connections", element: <DataConnectionsPage />, protected: true,
          label: "Data Connections", area: "reports/data-connections",
        }
      ]
    },
    {
      path: "royalty-splits", element: <RoyaltySplits />, protected: true,
      area: "royalty-splits", label: "Royalty Splits"
    },
    {
      path: "royalty-area-balances", element: <RoyaltyAreaBalances />, protected: true,
      area: "royalty-area-balances", label: "Royalty Area Balances"
    },
    {
      path: "targets",
      protected: true,
      superZoneAdminOnly: true,
      children: [
        {
          path: "imports",
          element: <TargetImportList />,
          protected: true,
          superZoneAdminOnly: true,
          label: "Target Imports",
        },
        {
          path: "imports/analyze",
          element: <TargetImportAnalyze />,
          protected: true,
          ignore: true,
          superZoneAdminOnly: true,
        },
        {
          path: "imports/templates/new",
          element: <TargetImportTemplateEditor />,
          protected: true,
          ignore: true,
          superZoneAdminOnly: true,
        },
        {
          path: "imports/templates/:id",
          element: <TargetImportTemplateEditor />,
          protected: true,
          ignore: true,
          superZoneAdminOnly: true,
        },
        {
          path: "imports/run/:templateId",
          element: <TargetImportRun />,
          protected: true,
          ignore: true,
          superZoneAdminOnly: true,
        },
      ]
    },
    {
      path: "admin",
      protected: true,
      children: [
        {
          path: "users", element: <Users />, protected: true,
          access: perms.ADMIN
        },
        {
          path: "references",
          protected: true,
          children: [
            {
              path: "analytes", element: <Analytes />, protected: true,
              area: "admin/references/analytes"
            },
            {
              path: "bucket-factors", element: <BucketFactors />, protected: true,
              area: "admin/references/bucket-factors"
            },
            {
              path: "bucket-types", element: <BucketTypes />, protected: true,
              area: "admin/references/bucket-types"
            },
            {
              path: "densities", element: <Densities />, protected: true,
              area: "admin/references/densities"
            },
            {
              path: "departments", element: <Departments />, protected: true,
              area: "admin/references/departments"
            },
            {
              path: "downtime-subtypes", element: <DowntimeSubtypes />, protected: true,
              area: "admin/references/downtime-subtypes"
            },
            {
              path: "material-types", element: <MaterialTypes />, protected: true,
              area: "admin/references/material-types"
            },
            {
              path: "material-type-classes", element: <MaterialTypeClasses />, protected: true,
              area: "admin/references/material-type-classes"
            },
            {
              path: "plant", element: <Plants />, protected: true,
              area: "admin/references/plant"
            },
            {
              path: "plant-types", element: <PlantTypes />, protected: true,
              area: "admin/references/plant-types"
            },
            // Note: spread rather than a `hidden` flag, so on a site without an FMS the route does not
            // exist at all - no menu entry AND no reachable URL. A flag the nav merely skips would still
            // leave the page open to anyone who typed the path.
            // Note: absent telemetryEnabled reads as off, which matters when a client is newer than the
            // API it is talking to: the screen stays hidden rather than appearing and failing.
            ...(telemetryEnabled ? [{
              path: "device-mappings", element: <DeviceMappings />, protected: true,
              area: "admin/references/device-mappings", label: "Device mappings"
            }] : []),
            {
              path: "pit-stages", element: <PitStages />, protected: true, label: "Pit stages",
              area: "admin/references/pit-stages"
            },
            {
              path: "locations", element: <Locations />, protected: true,
              area: "admin/references/locations"
            },
            {
              path: "targets", element: <Targets />, protected: true, label: "Targets and EOM Actuals",
              area: "admin/references/targets"
            },
            {
              path: "target-types", element: <TargetTypes />, protected: true, label: "Target Types",
              superZoneAdminOnly: true
            },
            {
              path: "mine-periods", element: <MinePeriods />, protected: true, label: "Mine Periods",
              superZoneAdminOnly: true
            },
            {
              path: "dashboard-tiles", element: <DashboardTiles />, protected: true, label: "Dashboard Tiles",
              superZoneAdminOnly: true
            },
            {
              path: "section-names", element: <SectionNames />, protected: true, label: "Section Names",
              superZoneAdminOnly: true
            }
          ]
        }
      ]
    },
    {
      path: "*",
      element: <NotFound />,
      protected: true
    }
  ];
}
